/**
 * Maps `GET /v2/portfolio/elite-pricing-details` slabs into Elite pricing-sheet tiers.
 *
 * Belongs here: portfolio-band labels, offer prices, active-row resolution.
 * Does not belong here: sheet chrome / CMS copy.
 */
import { roundOffAndFormatNumberWithCurrency } from '../../utils/utils'
import type {
  ElitePricingDetailsResponse,
  ElitePricingPortfolioSlab,
  ElitePricingSlabTier
} from './types/elitePricingDetails.types'

const ONE_LAKH = 100_000
const ONE_CRORE = 10_000_000

/** Compact slab labels: ₹10L / ₹1.5cr. */
function formatCompactPortfolioAmount(amount: number): string {
  if (amount >= ONE_CRORE) {
    const crores = amount / ONE_CRORE
    if (Number.isInteger(crores)) {
      return `₹${crores}cr`
    }
    return `₹${parseFloat(crores.toFixed(2))}cr`
  }
  if (amount >= ONE_LAKH) {
    const lakhs = amount / ONE_LAKH
    if (Number.isInteger(lakhs)) {
      return `₹${lakhs}L`
    }
    return `₹${parseFloat(lakhs.toFixed(2))}L`
  }
  return String(roundOffAndFormatNumberWithCurrency(amount))
}

function formatPortfolioSlabLabel(minAmount: number, maxAmount: number): string {
  if (maxAmount <= 0) {
    return `Above ${formatCompactPortfolioAmount(minAmount)}`
  }
  if (minAmount <= 0) {
    return `Upto ${formatCompactPortfolioAmount(maxAmount)}`
  }
  return `${formatCompactPortfolioAmount(minAmount)} - ${formatCompactPortfolioAmount(maxAmount)}`
}

function mapSlabToTier(
  slab: ElitePricingPortfolioSlab,
  index: number,
  listPrice: string
): ElitePricingSlabTier {
  const minAmount = slab.minPortfolioValue?.amount ?? 0
  const maxAmount = slab.maxPortfolioValue?.amount ?? 0
  const priceAmount = slab.price?.amount ?? 0

  return {
    id: `elite-slab-${index}`,
    portfolioLabel: formatPortfolioSlabLabel(minAmount, maxAmount),
    listPrice,
    price: `${roundOffAndFormatNumberWithCurrency(priceAmount)}`,
    priceAmount,
    minPortfolioAmount: minAmount,
    maxPortfolioAmount: maxAmount
  }
}

/** Portfolio band first; fee amount only if no band matches. */
function findActiveTierId(
  tiers: ElitePricingSlabTier[],
  feeAmount: number,
  portfolioAmount: number
): string {
  if (tiers.length === 0) return ''

  if (portfolioAmount > 0) {
    const bandMatch = tiers.find((tier) => {
      if (tier.maxPortfolioAmount <= 0) {
        return portfolioAmount >= tier.minPortfolioAmount
      }
      return (
        portfolioAmount >= tier.minPortfolioAmount && portfolioAmount < tier.maxPortfolioAmount
      )
    })
    if (bandMatch) return bandMatch.id
  }

  if (feeAmount > 0) {
    const feeMatch = tiers.find((tier) => tier.priceAmount === feeAmount)
    if (feeMatch) return feeMatch.id
  }

  return ''
}

/**
 * When the portfolio-band row’s API price differs from the live tenure fee
 * (PlanOptionCard / lock-in card), show the outside fee on that row.
 */
function applyOutsideFeeToActiveTier(
  tiers: ElitePricingSlabTier[],
  activeTierId: string,
  feeAmount: number
): ElitePricingSlabTier[] {
  if (!activeTierId || feeAmount <= 0) return tiers

  return tiers.map((tier) => {
    if (tier.id !== activeTierId || tier.priceAmount === feeAmount) return tier
    return {
      ...tier,
      price: `${roundOffAndFormatNumberWithCurrency(feeAmount)}`
    }
  })
}

/** Builds display tiers + portfolio row from the Elite pricing-details API payload. */
export function resolveElitePricingDetailsTiers({
  pricingDetails,
  feeAmount,
  listPrice
}: {
  pricingDetails: ElitePricingDetailsResponse | undefined
  feeAmount: number
  listPrice: string
}): { tiers: ElitePricingSlabTier[]; activeTierId: string; portfolioValue: string } {
  if (!pricingDetails) {
    return { tiers: [], activeTierId: '', portfolioValue: '' }
  }

  const slabs = pricingDetails.portfolioSlabList ?? []
  const mappedTiers = slabs.map((slab, index) => mapSlabToTier(slab, index, listPrice))
  // Portfolio row always comes from `currentPortfolioValue`
  const portfolioAmount = pricingDetails.currentPortfolioValue?.amount ?? 0
  const portfolioValue =
    portfolioAmount > 0 ? `${roundOffAndFormatNumberWithCurrency(portfolioAmount)}` : ''
  const activeTierId = findActiveTierId(mappedTiers, feeAmount, portfolioAmount)

  return {
    tiers: applyOutsideFeeToActiveTier(mappedTiers, activeTierId, feeAmount),
    activeTierId,
    portfolioValue
  }
}
